import React, { useEffect } from 'react';
import { View, Text, StyleSheet, TextStyle, ViewStyle, TouchableOpacity } from 'react-native';
import { OtpInput, OtpInputRef } from 'react-native-otp-entry';
import useTimer from './useTimer';

// Timer-specific props
interface TimerProps {
  /** Duration of the timer in seconds */
  timerDuration?: number;
  /** Custom style for the timer text */
  timerStyle?: TextStyle;
  /** Custom formatter for the timer display */
  timerFormatter?: (timeLeft: string) => string;
  /** Whether to show the timer */
  showTimer?: boolean;
  /** Position of the timer relative to OTP input */
  timerPosition?: 'top' | 'bottom';
  /** Callback when timer expires */
  onTimerExpire?: () => void;
  /** Callback when timer starts */
  onTimerStart?: () => void;
  /** Callback when timer resets */
  onTimerReset?: () => void;
  /** Whether to auto-start the timer */
  autoStartTimer?: boolean;
  /** Callback to restart the timer manually */
  onRestartTimer?: () => void;
  /** Show restart button when timer expires */
  showRestartButton?: boolean;
  /** Custom text for restart button */
  restartButtonText?: string;
  /** Custom style for restart button */
  restartButtonStyle?: TextStyle;
}

// All props from react-native-otp-entry
interface OtpEntryProps {
  /** Number of digits for the OTP input */
  numberOfDigits: number;
  /** Focus color for the input */
  focusColor?: string;
  /** Auto focus on mount */
  autoFocus?: boolean;
  /** Hide the focus stick */
  hideStick?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Blur on filled */
  blurOnFilled?: boolean;
  /** Disable the input */
  disabled?: boolean;
  /** Input type */
  type?: 'numeric' | 'alpha' | 'alphanumeric';
  /** Secure text entry */
  secureTextEntry?: boolean;
  /** Focus stick blinking duration */
  focusStickBlinkingDuration?: number;
  /** Callback when OTP is filled */
  onFilled?: (text: string) => void;
  /** Callback when text changes */
  onTextChange?: (text: string) => void;
  /** Text input props */
  textInputProps?: any;
  /** Text props */
  textProps?: any;
  /** Theme configuration */
  theme?: {
    containerStyle?: ViewStyle;
    inputsContainerStyle?: ViewStyle;
    pinCodeContainerStyle?: ViewStyle;
    pinCodeTextStyle?: TextStyle;
    focusStickStyle?: ViewStyle;
    focusedPinCodeContainerStyle?: ViewStyle;
    filledPinCodeContainerStyle?: ViewStyle;
  };
}

// Combined props interface
export interface OtpWithTimerProps extends OtpEntryProps, TimerProps {
  /** Container style for the entire component */
  containerStyle?: ViewStyle;
  /** Reference to the OTP input */
  ref?: React.Ref<OtpInputRef>;
}

const OtpWithTimer = React.forwardRef<OtpInputRef, OtpWithTimerProps>(
  (
    {
      // Timer props
      timerDuration = 60,
      timerStyle,
      timerFormatter,
      showTimer = true,
      timerPosition = 'top',
      onTimerExpire,
      onTimerStart,
      onTimerReset,
      onRestartTimer,
      autoStartTimer = true,
      showRestartButton = true,
      restartButtonText = 'Resend OTP',
      restartButtonStyle,
      containerStyle,
      
      // OTP Entry props
      numberOfDigits,
      focusColor,
      autoFocus,
      hideStick,
      placeholder,
      blurOnFilled,
      disabled,
      type,
      secureTextEntry,
      focusStickBlinkingDuration,
      onFilled,
      onTextChange,
      textInputProps,
      textProps,
      theme,
      ...rest
    },
    ref
  ) => {
    const { timeLeft, hasTimeout, startTimer, resetOtpTimer } = useTimer(timerDuration);

    const handleRestartTimer = () => {
      resetOtpTimer();
      startTimer();
      onRestartTimer?.();
      onTimerStart?.();
    };

    useEffect(() => {
      if (autoStartTimer) {
        startTimer();
        onTimerStart?.();
      }
    }, [autoStartTimer, startTimer, onTimerStart]);

    useEffect(() => {
      if (hasTimeout) {
        onTimerExpire?.();
      }
    }, [hasTimeout, onTimerExpire]);

    const handleFilled = (text: string) => {
      onFilled?.(text);
    };

    const handleTextChange = (text: string) => {
      onTextChange?.(text);
    };

    const formatTimerDisplay = (time: string) => {
      return timerFormatter ? timerFormatter(time) : time;
    };

    const renderTimer = () => {
      if (!showTimer) return null;

      return (
        <Text style={[styles.timerText, timerStyle]}>
          {hasTimeout ? 'Timer expired' : formatTimerDisplay(timeLeft)}
        </Text>
      );
    };

    return (
      <View style={[styles.container, containerStyle]}>
        {timerPosition === 'top' && renderTimer()}
        
        <OtpInput
          ref={ref}
          numberOfDigits={numberOfDigits}
          focusColor={focusColor}
          autoFocus={autoFocus}
          hideStick={hideStick}
          placeholder={placeholder}
          blurOnFilled={blurOnFilled}
          disabled={disabled || hasTimeout}
          type={type}
          secureTextEntry={secureTextEntry}
          focusStickBlinkingDuration={focusStickBlinkingDuration}
          onFilled={handleFilled}
          onTextChange={handleTextChange}
          textInputProps={textInputProps}
          textProps={textProps}
          theme={theme}
          {...rest}
        />
        
        {timerPosition === 'bottom' && renderTimer()}
        
        {hasTimeout && (
          <View style={styles.expiredContainer}>
            <Text style={styles.expiredText}>
              Time expired. Please request a new OTP.
            </Text>
            {showRestartButton && (
              <TouchableOpacity onPress={handleRestartTimer}>
                <Text style={[styles.restartButton, restartButtonStyle]}>
                  {restartButtonText}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    );
  }
);

// Expose the timer control methods
export const useOtpTimer = (timerDuration: number = 60) => {
  const timerHook = useTimer(timerDuration);
  
  const restartTimer = () => {
    timerHook.resetOtpTimer();
    timerHook.startTimer();
  };
  
  return {
    ...timerHook,
    resetTimer: timerHook.resetOtpTimer,
    restartTimer,
  };
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  timerText: {
    fontSize: 16,
    color: '#333',
    marginVertical: 10,
    textAlign: 'center',
  },
  expiredContainer: {
    alignItems: 'center',
    marginTop: 10,
  },
  expiredText: {
    fontSize: 12,
    color: '#FF0000',
    textAlign: 'center',
    marginBottom: 8,
  },
  restartButton: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '600',
    textDecorationLine: 'underline',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
});

OtpWithTimer.displayName = 'OtpWithTimer';

export default OtpWithTimer;
